import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Trash2, Plus, Save, Sparkles, X, Check } from 'lucide-react';

const mentionPattern = /@\[(circle|role|project):(\d+)\|([^\]]+)\]/g;

const buildMentionSegments = (rawText) => {
  if (!rawText) return [];
  const segments = [];
  let lastIndex = 0;
  rawText.replace(mentionPattern, (match, type, id, label, offset) => {
    if (offset > lastIndex) {
      const text = rawText.slice(lastIndex, offset);
      segments.push({ type: 'text', raw: text, display: text });
    }
    const display = `@${label}`;
    segments.push({ type: 'mention', raw: match, display, mentionType: type, id: Number(id), label });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < rawText.length) {
    const text = rawText.slice(lastIndex);
    segments.push({ type: 'text', raw: text, display: text });
  }
  return segments;
};

const segmentsToDisplay = (segments) => segments.map((seg) => seg.display).join('');

const segmentsToRaw = (segments) => segments.map((seg) => seg.raw).join('');

const splitSegmentsAt = (segments, index) => {
  if (index <= 0) return { left: [], right: [...segments] };
  let remaining = index;
  const left = [];
  let right = [];

  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    const length = segment.display.length;
    if (remaining > length) {
      left.push(segment);
      remaining -= length;
      continue;
    }
    if (remaining === length) {
      left.push(segment);
      right = segments.slice(i + 1);
      return { left, right };
    }
    if (remaining === 0) {
      right = segments.slice(i);
      return { left, right };
    }
    const before = segment.display.slice(0, remaining);
    const after = segment.display.slice(remaining);
    if (before) {
      left.push({ type: 'text', raw: before, display: before });
    }
    if (after) {
      right = [{ type: 'text', raw: after, display: after }, ...segments.slice(i + 1)];
    } else {
      right = segments.slice(i + 1);
    }
    return { left, right };
  }
  return { left, right };
};

const mentionStyles = {
  circle: 'text-indigo-600 bg-indigo-50/70',
  role: 'text-amber-600 bg-amber-50/70',
  project: 'text-emerald-600 bg-emerald-50/70',
};

const domainQuickTags = ['#facturabilite', '#NotifFacture'];

const MentionInput = ({ value, onChange, onCommit, onCancel, mentionItems, placeholder, inputRef, getMentionStyle }) => {
  const [rawDraft, setRawDraft] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [activeQuery, setActiveQuery] = useState(null);
  const overlayRef = useRef(null);
  const ignoreBlurRef = useRef(false);

  useEffect(() => {
    setRawDraft(value || '');
  }, [value]);

  const segments = useMemo(() => buildMentionSegments(rawDraft), [rawDraft]);
  const displayDraft = useMemo(() => segmentsToDisplay(segments), [segments]);

  const updateSuggestions = (text, caretPos) => {
    const before = text.slice(0, caretPos);
    const match = before.match(/@([^\s,]*)$/);
    if (!match) {
      setSuggestions([]);
      setActiveQuery(null);
      return;
    }
    const query = match[1] || '';
    if (query.startsWith('[')) {
      setSuggestions([]);
      setActiveQuery(null);
      return;
    }
    const start = caretPos - match[0].length;
    const filtered = (mentionItems || []).filter((item) =>
      item.name.toLowerCase().includes(query.toLowerCase())
    );
    setActiveQuery({ start, end: caretPos, query });
    setSuggestions(filtered.slice(0, 6));
  };

  const applyDisplayChange = (nextDisplay, caretPos) => {
    const prevDisplay = displayDraft;
    if (nextDisplay === prevDisplay) return;
    let start = 0;
    while (
      start < prevDisplay.length &&
      start < nextDisplay.length &&
      prevDisplay[start] === nextDisplay[start]
    ) {
      start += 1;
    }
    let endPrev = prevDisplay.length - 1;
    let endNext = nextDisplay.length - 1;
    while (
      endPrev >= start &&
      endNext >= start &&
      prevDisplay[endPrev] === nextDisplay[endNext]
    ) {
      endPrev -= 1;
      endNext -= 1;
    }
    const removedCount = Math.max(0, endPrev - start + 1);
    const inserted = nextDisplay.slice(start, endNext + 1);

    const { left, right: rest } = splitSegmentsAt(segments, start);
    const { right } = splitSegmentsAt(rest, removedCount);
    const nextSegments = [...left];
    if (inserted) {
      nextSegments.push({ type: 'text', raw: inserted, display: inserted });
    }
    nextSegments.push(...right);
    const nextRaw = segmentsToRaw(nextSegments);
    setRawDraft(nextRaw);
    onChange && onChange(nextRaw);
    updateSuggestions(nextDisplay, caretPos);
  };

  const handleChange = (event) => {
    const nextDisplay = event.target.value;
    const caretPos = event.target.selectionStart ?? nextDisplay.length;
    applyDisplayChange(nextDisplay, caretPos);
  };

  const applyMention = (item) => {
    if (!activeQuery) return;
    const safeLabel = (item.name || '').replace(/\|/g, '').replace(/\]/g, '');
    const token = `@[${item.type}:${item.id}|${safeLabel}]`;
    const displayToken = `@${safeLabel}`;
    const { left, right: rest } = splitSegmentsAt(segments, activeQuery.start);
    const { right } = splitSegmentsAt(rest, activeQuery.end - activeQuery.start);
    const nextSegments = [...left, { type: 'mention', raw: token, display: displayToken }, ...right];
    const nextRaw = segmentsToRaw(nextSegments);
    setRawDraft(nextRaw);
    onChange && onChange(nextRaw);
    setSuggestions([]);
    setActiveQuery(null);
    requestAnimationFrame(() => {
      const pos = segmentsToDisplay(left).length + displayToken.length;
      if (inputRef?.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handleBlur = () => {
    if (ignoreBlurRef.current) return;
    setSuggestions([]);
    setActiveQuery(null);
    onCommit && onCommit(rawDraft);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setSuggestions([]);
      setActiveQuery(null);
      onCancel && onCancel();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (suggestions.length > 0 && activeQuery) {
        applyMention(suggestions[0]);
        return;
      }
      onCommit && onCommit(rawDraft);
    }
  };

  const handleScroll = (event) => {
    if (!overlayRef.current) return;
    overlayRef.current.scrollLeft = event.target.scrollLeft;
  };

  return (
    <div className="relative w-full">
      <div
        ref={overlayRef}
        className="absolute inset-0 px-3 py-1.5 text-sm rounded-xl pointer-events-none overflow-hidden whitespace-pre text-slate-700"
      >
        {displayDraft ? (
          segments.map((segment, index) => {
            if (segment.type === 'mention') {
              const mentionClass = mentionStyles[segment.mentionType] || 'text-slate-600 bg-slate-100';
              const mentionStyle = getMentionStyle ? getMentionStyle(segment.mentionType, segment.id) : null;
              return (
                <span
                  key={`seg-${index}`}
                  className={`font-semibold px-1 rounded ${mentionClass}`}
                  style={mentionStyle || undefined}
                >
                  {segment.display}
                </span>
              );
            }
            return <span key={`seg-${index}`}>{segment.display}</span>;
          })
        ) : (
          <span className="text-slate-300">{placeholder}</span>
        )}
      </div>
      <input
        ref={inputRef}
        type="text"
        value={displayDraft}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onScroll={handleScroll}
        className="w-full bg-white border-2 border-emerald-300 rounded-xl px-3 py-1.5 text-sm text-transparent caret-slate-800 focus:outline-none shadow-sm"
        placeholder=""
      />
      {suggestions.length > 0 && (
        <div className="absolute z-20 mt-2 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
          {suggestions.map((item) => (
            <button
              key={`${item.type}-${item.id}`}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                ignoreBlurRef.current = true;
                applyMention(item);
                setTimeout(() => {
                  ignoreBlurRef.current = false;
                }, 0);
              }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
            >
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] border ${mentionStyles[item.type] || 'bg-slate-100 text-slate-600 border-slate-200'}`}
                style={getMentionStyle ? getMentionStyle(item.type, item.id) : undefined}
              >
                {item.type}
              </span>
              <span className="text-slate-700 truncate">{item.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const EditableMentionList = ({ items, onChange, placeholder, mentionItems, getMentionStyle }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editingIndex !== null && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingIndex]);

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setEditValue(items[index] || '');
  };

  const handleSave = (nextValue = editValue) => {
    if (editingIndex === null) return;
    const newItems = [...items];
    const trimmed = String(nextValue || '').trim();
    if (trimmed) {
      newItems[editingIndex] = trimmed;
    } else {
      newItems.splice(editingIndex, 1);
    }
    onChange(newItems);
    setEditingIndex(null);
    setEditValue('');
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  const handleAdd = () => {
    const newItems = [...items, ''];
    onChange(newItems);
    setEditingIndex(newItems.length - 1);
    setEditValue('');
  };

  const handleDelete = (e, index) => {
    e.stopPropagation();
    const newItems = [...items];
    newItems.splice(index, 1);
    onChange(newItems);
  };

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, index) => {
        if (editingIndex === index) {
          return (
            <div key={index} className="relative w-52">
              <MentionInput
                value={editValue}
                onChange={setEditValue}
                onCommit={(nextValue) => handleSave(nextValue)}
                onCancel={handleCancel}
                mentionItems={mentionItems}
                placeholder={placeholder}
                inputRef={inputRef}
                getMentionStyle={getMentionStyle}
              />
            </div>
          );
        }
        const displayValue = segmentsToDisplay(buildMentionSegments(item || ''));
        return (
          <div
            key={index}
            onClick={() => handleStartEdit(index)}
            className="group flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md hover:border-emerald-200 cursor-pointer transition-all duration-200"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
            <span className="text-sm font-medium text-slate-700">{displayValue}</span>
            <button
              onClick={(e) => handleDelete(e, index)}
              className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all p-0.5 rounded-full hover:bg-red-50"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}

      <button
        type="button"
        onClick={handleAdd}
        className="flex items-center justify-center w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:scale-105 transition-all duration-200 border border-emerald-100"
        title="Ajouter une redevabilite"
      >
        <Plus size={18} />
      </button>
    </div>
  );
};

// --- Sub-component for Inline Editable List ---
const EditableTagList = ({ items, onChange, placeholder, colorClass, iconColor, quickTags = [] }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editingIndex !== null && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingIndex]);

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setEditValue(items[index]);
  };

  const handleSave = () => {
    if (editingIndex === null) return;

    const newItems = [...items];
    const trimmed = editValue.trim();

    if (trimmed) {
      newItems[editingIndex] = trimmed;
    } else {
      // Remove if empty
      newItems.splice(editingIndex, 1);
    }

    onChange(newItems);
    setEditingIndex(null);
    setEditValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditingIndex(null);
      setEditValue('');
    }
  };

  const handleAdd = () => {
    const newItems = [...items, ''];
    onChange(newItems);
    setEditingIndex(newItems.length - 1);
    setEditValue('');
  };

  const handleAddQuickTag = (tag) => {
    const normalized = String(tag || '').trim();
    if (!normalized) return;
    if ((items || []).some((item) => String(item).trim().toLowerCase() === normalized.toLowerCase())) return;
    onChange([...(items || []), normalized]);
  };

  const handleDelete = (e, index) => {
    e.stopPropagation(); // Prevent triggering edit
    const newItems = [...items];
    newItems.splice(index, 1);
    onChange(newItems);
  };

  return (
    <div className="flex flex-wrap gap-2">
      {quickTags.map((tag) => (
        <button
          key={tag}
          type="button"
          onClick={() => handleAddQuickTag(tag)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-100 bg-slate-50 text-sm font-semibold text-slate-700 hover:bg-white hover:border-indigo-200 transition-all"
        >
          {tag}
        </button>
      ))}
      {items.map((item, index) => (
        <div key={index} className="relative">
          {editingIndex === index ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              className="w-40 bg-white border-2 border-indigo-300 rounded-xl px-3 py-1.5 text-sm text-slate-800 focus:outline-none shadow-sm"
              placeholder={placeholder}
            />
          ) : (
            <div
              onClick={() => handleStartEdit(index)}
              className={`group flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md hover:border-${colorClass}-200 cursor-pointer transition-all duration-200`}
            >
              <div className={`w-1.5 h-1.5 rounded-full bg-${iconColor}-400`}></div>
              <span className="text-sm font-medium text-slate-700">{item}</span>
              <button
                onClick={(e) => handleDelete(e, index)}
                className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all p-0.5 rounded-full hover:bg-red-50"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={handleAdd}
        className={`flex items-center justify-center w-8 h-8 rounded-xl bg-${colorClass}-50 text-${colorClass}-600 hover:bg-${colorClass}-100 hover:scale-105 transition-all duration-200 border border-${colorClass}-100`}
        title="Ajouter un item"
      >
        <Plus size={18} />
      </button>
    </div>
  );
};


// --- Sub-component for Inline Editable Strategy List ---
const EditableStrategyList = ({ items, onChange, placeholder, colorClass, iconColor }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editingIndex !== null && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingIndex]);

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setEditValue(items[index].content);
  };

  const handleSave = () => {
    if (editingIndex === null) return;

    const newItems = [...items];
    const trimmed = editValue.trim();

    if (trimmed) {
      newItems[editingIndex] = {
        ...newItems[editingIndex],
        content: trimmed,
        updated_at: new Date().toISOString() // Update timestamp
      };
    } else {
      // Remove if empty
      newItems.splice(editingIndex, 1);
    }

    onChange(newItems);
    setEditingIndex(null);
    setEditValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditingIndex(null);
      setEditValue('');
    }
  };

  const handleAdd = () => {
    const newItems = [...items, { content: '', updated_at: new Date().toISOString() }];
    onChange(newItems);
    setEditingIndex(newItems.length - 1);
    setEditValue('');
  };

  const handleDelete = (e, index) => {
    e.stopPropagation(); // Prevent triggering edit
    const newItems = [...items];
    newItems.splice(index, 1);
    onChange(newItems);
  };

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => (
        <div key={index} className="relative w-full">
          {editingIndex === index ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              className="w-full bg-white border-2 border-amber-300 rounded-xl px-3 py-2 text-sm text-slate-800 focus:outline-none shadow-sm"
              placeholder={placeholder}
            />
          ) : (
            <div
              onClick={() => handleStartEdit(index)}
              className={`group flex items-center justify-between gap-2 px-4 py-2 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md hover:border-${colorClass}-200 cursor-pointer transition-all duration-200`}
            >
              <div className="flex items-center gap-3 flex-1">
                <div className={`w-2 h-2 rounded-full bg-${iconColor}-400`}></div>
                <span className="text-sm font-medium text-slate-700">{item.content}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  {new Date(item.updated_at).toLocaleDateString()}
                </span>
                <button
                  onClick={(e) => handleDelete(e, index)}
                  className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all p-1 rounded-full hover:bg-red-50"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={handleAdd}
        className={`flex items-center justify-center gap-2 w-full py-2 rounded-xl bg-${colorClass}-50 text-${colorClass}-600 hover:bg-${colorClass}-100 transition-all duration-200 border border-${colorClass}-100 border-dashed`}
        title="Ajouter un point de stratégie"
      >
        <Plus size={16} />
        <span className="text-sm font-medium">Ajouter un point</span>
      </button>
    </div>
  );
};


const CircleForm = ({ circle, employees, mentionItems = [], getMentionStyle, onSave, onDelete, onCancel, parentId, isRole }) => {
  const [formData, setFormData] = useState({
    name: '',
    purpose: '',
    domains: [],
    accountabilities: [],
    strategy: [],
    owner_id: '',
    owner_ids: [],
    parent_id: parentId || null,
  });

  useEffect(() => {
    if (circle) {
      setFormData({
        name: circle.name || '',
        purpose: circle.purpose || '',
        domains: circle.domains || [],
        accountabilities: circle.accountabilities || [],
        strategy: circle.strategy || [],
        owner_id: circle.owner_id || '',
        owner_ids: circle.owner_ids || (circle.owner_id ? [circle.owner_id] : []),
        parent_id: circle.parent_id || parentId || null,
      });
    } else {
      setFormData({
        name: '',
        purpose: '',
        domains: [],
        accountabilities: [],
        strategy: [],
        owner_id: '',
        owner_ids: [],
        parent_id: parentId || null,
      });
    }
  }, [circle, parentId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleListUpdate = (listName, newItems) => {
    setFormData(prev => ({ ...prev, [listName]: newItems }));
  };

  const Avatar = ({ employee, size = 36 }) => {
    const initial = employee?.name?.charAt(0) || '?';
    const picture =
      employee?.picture ||
      employee?.google_picture ||
      employee?.google_picture_url ||
      employee?.photo_url ||
      employee?.avatar ||
      employee?.image;
    return (
      <div
        className="rounded-full bg-indigo-500 text-white flex items-center justify-center overflow-hidden"
        style={{ width: size, height: size }}
      >
        {picture ? (
          <img src={picture} alt={employee.name} className="w-full h-full object-cover" />
        ) : (
          <span className="font-semibold text-sm">{initial}</span>
        )}
      </div>
    );
  };

  const activeEmployees = React.useMemo(
    () => employees.filter((emp) => emp?.is_active !== false),
    [employees]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanedOwnerIds = (formData.owner_ids || []).map((id) => Number(id)).filter((id) => !Number.isNaN(id));
    const cleanedOwnerId = formData.owner_id === '' || formData.owner_id === null || formData.owner_id === undefined
      ? null
      : Number(formData.owner_id);
    const hasOwnerId = cleanedOwnerId !== null && !Number.isNaN(cleanedOwnerId);
    let ownerIds = isRole ? cleanedOwnerIds : (hasOwnerId ? [cleanedOwnerId] : []);
    if (!isRole && ownerIds.length > 1) {
      ownerIds = [ownerIds[0]];
    }
    if (hasOwnerId && !ownerIds.includes(cleanedOwnerId)) {
      ownerIds = [cleanedOwnerId, ...ownerIds];
    }
    const primaryOwnerId = hasOwnerId ? cleanedOwnerId : (ownerIds[0] ?? null);

    const sanitized = {
      ...formData,
      owner_id: primaryOwnerId,
      owner_ids: ownerIds,
      domains: (formData.domains || []).map((d) => d.trim()).filter(Boolean),
      accountabilities: (formData.accountabilities || []).map((a) => a.trim()).filter(Boolean),
      strategy: (formData.strategy || []).filter(s => s.content && s.content.trim()),
    };
    onSave(sanitized);
  };

  return (
    <div className="relative h-full overflow-y-auto rounded-3xl bg-white/80 backdrop-blur-2xl shadow-2xl border border-white/50 p-8">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 mb-8 flex items-start justify-between">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-xs font-bold uppercase tracking-wider mb-3">
            <Sparkles size={12} />
            <span>Edition Mode</span>
          </div>
          <h2 className="text-3xl font-extrabold text-slate-800 tracking-tight">
            {circle ? 'Modifier le Cercle' : 'Nouveau Cercle'}
          </h2>
          <p className="mt-2 text-slate-500 font-medium">
            Configurez la structure et les rôles de ce cercle.
          </p>
        </div>
        {circle && (
          <button
            onClick={() => onDelete(circle.id)}
            className="group flex items-center justify-center w-10 h-10 rounded-full bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-all duration-300 shadow-sm hover:shadow-red-200"
            title="Supprimer le cercle"
          >
            <Trash2 size={18} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="relative z-10 space-y-8">
        {/* Main Info Section */}
        <div className="space-y-6">
          <div className="group relative">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">Nom du Cercle</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full bg-white/50 border border-slate-200 rounded-2xl px-5 py-4 text-xl font-bold text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all duration-300"
              placeholder="Ex: Produit & Tech"
            />
          </div>

          <div className="group relative">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">Raison d'être</label>
            <textarea
              name="purpose"
              value={formData.purpose}
              onChange={handleChange}
              rows={3}
              className="w-full bg-white/50 border border-slate-200 rounded-2xl px-5 py-4 text-slate-700 placeholder-slate-300 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all duration-300 resize-none"
              placeholder="Quelle est la mission fondamentale de ce cercle ?"
            />
          </div>

          
          <div className="group relative">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">
              {isRole ? 'Leaders du role' : 'Leader du Cercle'}
            </label>
            <div className="relative">
              <div className="grid grid-cols-1 gap-2">
                {activeEmployees.map((emp) => {
                  const isSelected = isRole
                    ? (formData.owner_ids || []).map(Number).includes(Number(emp.id))
                    : Number(formData.owner_id) === Number(emp.id);
                  const toggle = () => {
                    if (isRole) {
                      const current = (formData.owner_ids || []).map(Number);
                      const next = current.includes(Number(emp.id))
                        ? current.filter((id) => id !== Number(emp.id))
                        : [...current, Number(emp.id)];
                      setFormData((prev) => ({ ...prev, owner_ids: next }));
                    } else {
                      setFormData((prev) => ({ ...prev, owner_id: emp.id }));
                    }
                  };
                  return (
                    <button
                      key={emp.id}
                      type="button"
                      onClick={toggle}
                      className={`flex items-center gap-3 p-3 rounded-2xl border transition ${
                        isSelected ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white hover:border-indigo-200'
                      }`}
                    >
                      <Avatar employee={emp} />
                      <div className="flex flex-col text-left">
                        <span className="text-sm font-semibold text-slate-800">{emp.name}</span>
                        <span className="text-xs text-slate-500">{isRole ? 'Remplisseur de role' : 'Leader'}</span>
                      </div>
                    </button>
                  );
                })}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, owner_id: prev.owner_id, owner_ids: prev.owner_ids }))}
                    className="mt-1 inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold text-slate-600 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

        {/* Strategy Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              Stratégie
            </label>
            <span className="text-xs text-slate-400 font-medium px-2 py-1 bg-slate-100 rounded-md">
              {formData.strategy.length} points
            </span>
          </div>

          <EditableStrategyList
            items={formData.strategy}
            onChange={(newItems) => handleListUpdate('strategy', newItems)}
            placeholder="Nouveau point stratégique"
            colorClass="amber"
            iconColor="amber"
          />
        </div>

        <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

        {/* Domains Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
              Domaines d'autorité
            </label>
            <span className="text-xs text-slate-400 font-medium px-2 py-1 bg-slate-100 rounded-md">
              {formData.domains.length} domaines
            </span>
          </div>

          <EditableTagList
            items={formData.domains}
            onChange={(newItems) => handleListUpdate('domains', newItems)}
            placeholder="Nouveau domaine"
            colorClass="indigo"
            iconColor="indigo"
            quickTags={domainQuickTags}
          />
        </div>

        {/* Accountabilities Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              Redevabilités
            </label>
            <span className="text-xs text-slate-400 font-medium px-2 py-1 bg-slate-100 rounded-md">
              {formData.accountabilities.length} items
            </span>
          </div>

          <EditableMentionList
            items={formData.accountabilities}
            onChange={(newItems) => handleListUpdate('accountabilities', newItems)}
            placeholder="Nouvelle redevabilite"
            mentionItems={mentionItems.filter((item) => item.type === 'role' || item.type === 'circle')}
            getMentionStyle={getMentionStyle}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 text-sm font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-xl transition-all duration-300"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white rounded-xl font-bold shadow-lg shadow-indigo-200 hover:shadow-xl hover:shadow-indigo-300 transform hover:-translate-y-0.5 transition-all duration-300"
          >
            <Check size={18} />
            Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
};

export default CircleForm;

